<template>
  <v-container fluid class="pa-2">
    <v-card outlined class="compact-card">
      <v-card-title class="d-flex justify-space-between align-center pa-3">
        <span class="text-subtitle-2 font-weight-bold">Tenant Age Analysis Report</span>
        <v-btn color="primary" small @click="generateReport" :loading="isGenerating" class="caption">
          <v-icon left x-small>mdi-refresh</v-icon> Generate
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-3">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="4">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field 
                  v-model="startDate" 
                  label="As Of Date" 
                  prepend-icon="mdi-calendar" 
                  readonly outlined dense
                  hide-details
                  class="small-text-field"
                  v-bind="attrs" 
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startDate" @input="startDateMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-switch
              v-model="showCurrent"
              label="Include Current"
              dense
              hide-details
              class="mt-0 small-switch"
              color="primary"
            ></v-switch>
          </v-col>
        </v-row>

        <div v-if="reportData" class="mt-4">
          <v-card outlined class="compact-table-card">
            <v-card-text style="max-height: 450px; overflow-y: auto; padding: 6px;">
              <v-simple-table dense class="age-analysis-table">
                <thead>
                  <tr>
                    <th class="caption font-weight-bold text-left">REF</th>
                    <th class="caption font-weight-bold text-left">PROPERTY (TENANT)</th>
                    <th v-if="showCurrent" class="caption font-weight-bold text-right">Current</th>
                    <th class="caption font-weight-bold text-right">0-30 Days</th>
                    <th class="caption font-weight-bold text-right">31-60 Days</th>
                    <th class="caption font-weight-bold text-right">61-90 Days</th>
                    <th class="caption font-weight-bold text-right">90+ Days</th>
                    <th class="caption font-weight-bold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in reportData" :key="item.reference">
                    <td class="caption">{{ item.reference }}</td>
                    <td class="caption text-left">{{ item.property }}</td>
                    <td v-if="showCurrent" :class="['caption text-right', { 'negative-balance': item.buckets.current < 0 }]">
                      {{ formatCurrency(item.buckets.current) }}
                    </td>
                    <td :class="['caption text-right', { 'negative-balance': item.buckets.days30 < 0 }]">
                      {{ formatCurrency(item.buckets.days30) }}
                    </td>
                    <td :class="['caption text-right', { 'negative-balance': item.buckets.days60 < 0 }]">
                      {{ formatCurrency(item.buckets.days60) }}
                    </td>
                    <td :class="['caption text-right', { 'negative-balance': item.buckets.days90 < 0 }]">
                      {{ formatCurrency(item.buckets.days90) }}
                    </td>
                    <td :class="['caption text-right', { 'negative-balance': item.buckets.days90plus < 0 }]">
                      {{ formatCurrency(item.buckets.days90plus) }}
                    </td>
                    <td :class="['caption text-right font-weight-bold', { 'negative-balance': item.total < 0 }]">
                      {{ formatCurrency(item.total) }}
                    </td>
                  </tr>
                  <tr class="total-row">
                    <td colspan="2" class="caption font-weight-bold">Totals</td>
                    <td v-if="showCurrent" class="caption font-weight-bold text-right">{{ formatCurrency(totals.current) }}</td>
                    <td class="caption font-weight-bold text-right">{{ formatCurrency(totals.days30) }}</td>
                    <td class="caption font-weight-bold text-right">{{ formatCurrency(totals.days60) }}</td>
                    <td class="caption font-weight-bold text-right">{{ formatCurrency(totals.days90) }}</td>
                    <td class="caption font-weight-bold text-right">{{ formatCurrency(totals.days90plus) }}</td>
                    <td class="caption font-weight-bold text-right">{{ formatCurrency(totals.total) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>

            <v-card-actions class="justify-end pa-2" style="gap: 6px;">
              <excel-export-component 
                :report-data="formattedReportData" 
                report-title="Tenant Age Analysis Report"
                :property-name="getReportTitle()" 
                :start-date="startDate" 
                :visible-columns="visibleColumns"
                :sum-field="sumFields" 
                :format-currency="formatCurrency" 
                :format-date="formatDate"
                :column-definitions="columnDefinitions" 
                @success="handleExportSuccess" 
                @error="handleExportError" 
                small
              />

              <PDFExportComponent 
                :report-data="formattedReportData" 
                report-title="Tenant Age Analysis Report"
                :property-name="getReportTitle()" 
                :start-date="startDate"
                :end-date="startDate" 
                :logo-base64="base64log"
                :visible-columns="visibleColumns" 
                :sum-field="sumFields" 
                :format-currency="formatCurrency"
                :format-date="formatDate" 
                :column-definitions="columnDefinitions" 
                @success="handleExportSuccess" 
                @error="handleExportError" 
                small
              />
            </v-card-actions>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import ExcelExportComponent from '@/views/ExcelExportComponent.vue';
import PDFExportComponent from '@/views/PDFExportComponent.vue';

const db = getFirestore();

export default {
  name: "AgeAnalysisReport",
  components: {
    ExcelExportComponent,
    PDFExportComponent
  },
  props: {
    property: {
      type: Object,
      default: null
    },
    unit: {
      type: Object,
      default: null
    },
    landlord: {
      type: Object,
      default: null
    }
  },
  data() {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    return {
      propertyName: this.property ? this.property.displayName || 'Unknown Property' : '',
      propertyId: this.property ? this.property.id || '' : '',
      landlordName: this.landlord ? this.landlord.name || 'Unknown Landlord' : '',
      landlordId: this.landlord ? this.landlord.id || '' : '',
      startDate: formattedDate,
      startDateMenu: false,
      showCurrent: false,
      isGenerating: false,
      reportData: null,
      base64log: "",
      totals: {},
      landlordProperties: []
    };
  },
  computed: {
    // Object keyed by field name, with a boolean per column.
    // This is what PDFExportComponent and ExcelExportComponent expect.
    visibleColumns() {
      const vis = {
        reference: true,
        property: true,
        days30: true,
        days60: true,
        days90: true,
        days90plus: true,
        total: true
      };
      if (this.showCurrent) {
        vis.current = true;
      }
      return vis;
    },
    formattedReportData() {
      if (!this.reportData) return [];
      return this.reportData.map(item => {
        const row = {
          reference: item.reference,
          property: item.property,
          days30: item.buckets.days30,
          days60: item.buckets.days60,
          days90: item.buckets.days90,
          days90plus: item.buckets.days90plus,
          total: item.total
        };
        if (this.showCurrent) {
          row.current = item.buckets.current;
        }
        return row;
      });
    },
    columnDefinitions() {
      const cols = [
        { field: 'reference', header: 'REF', alwaysVisible: true },
        { field: 'property', header: 'PROPERTY (TENANT)', alwaysVisible: true }
      ];

      if (this.showCurrent) {
        cols.push({ field: 'current', header: 'Current', formatter: this.formatCurrency });
      }

      cols.push(
        { field: 'days30', header: '0-30 Days', formatter: this.formatCurrency },
        { field: 'days60', header: '31-60 Days', formatter: this.formatCurrency },
        { field: 'days90', header: '61-90 Days', formatter: this.formatCurrency },
        { field: 'days90plus', header: '90+ Days', formatter: this.formatCurrency },
        { field: 'total', header: 'Total', formatter: this.formatCurrency }
      );

      return cols;
    },
    sumFields() {
      const fields = ['days30', 'days60', 'days90', 'days90plus', 'total'];
      if (this.showCurrent) fields.unshift('current');
      return fields;
    }
  },
  methods: {
    getReportTitle() {
      if (this.landlord) {
        return this.landlordName;
      } else if (this.property) {
        return this.propertyName;
      }
      return 'All Properties';
    },

    async generateReport() {
      this.isGenerating = true;
      this.reportData = null;

      try {
        const asOfDate = new Date(this.startDate);
        asOfDate.setHours(23, 59, 59, 999);

        let leasesQuery;

        if (this.landlord) {
          const propertiesQuery = query(
            collection(db, "properties"),
            where("selectedLandlord", "==", this.landlordId)
          );
          const propertiesSnapshot = await getDocs(propertiesQuery);

          this.landlordProperties = propertiesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          const propertyIds = this.landlordProperties.map(p => p.id);

          if (propertyIds.length === 0) {
            this.reportData = [];
            return;
          }

          leasesQuery = query(
            collection(db, "Leases"),
            where("propertyId", "in", propertyIds)
          );
        } else if (this.property) {
          leasesQuery = query(
            collection(db, "Leases"),
            where("propertyId", "==", this.propertyId)
          );

          if (this.unit && this.unit.unitName) {
            leasesQuery = query(leasesQuery, where("address", "==", this.unit.unitName));
          }
        } else {
          console.error("No landlord or property selected");
          return;
        }

        const leasesSnapshot = await getDocs(leasesQuery);
        const reportData = [];

        for (const leaseDoc of leasesSnapshot.docs) {
          const leaseData = leaseDoc.data();
          const endDateStr = leaseData.leaseDetails?.endDate;

          let isActive = true;
          if (endDateStr) {
            const endDate = new Date(endDateStr);
            if (!isNaN(endDate.getTime())) {
              if (endDate < asOfDate) {
                isActive = false;
              }
            }
          }

          if (!isActive) continue;

          const leaseRef = leaseData.leaseReference;
          const tenantName = leaseData.tenantName || "Unknown Tenant";

          let propertyName = "Unknown Property";
          if (this.landlord) {
            const property = this.landlordProperties.find(p => p.id === leaseData.propertyId);
            propertyName = property ? property.displayName : "Unknown Property";
          } else {
            propertyName = this.propertyName;
          }
          propertyName = `${propertyName} (${tenantName})`;

          // Query matches the statement view — one where + orderBy.
          // All charges for the lease are loaded (past and future) so Current
          // can include future-dated charges.
          const chargesQuery = query(
            collection(db, "Transactions"),
            where("leaseReference", "==", leaseRef),
            orderBy("datePaid")
          );
          const chargesSnapshot = await getDocs(chargesQuery);

          const buckets = { current: 0, days30: 0, days60: 0, days90: 0, days90plus: 0 };

          // Boundaries based on the as-of date, using the "2nd of the month" rule.
          let boundary;
          if (asOfDate.getDate() === 1) {
            boundary = new Date(asOfDate.getFullYear(), asOfDate.getMonth() - 1, 2);
          } else {
            boundary = new Date(asOfDate.getFullYear(), asOfDate.getMonth(), 2);
          }
          const boundaries = [boundary];
          for (let i = 1; i <= 4; i++) {
            boundaries.push(new Date(asOfDate.getFullYear(), asOfDate.getMonth() - i, 2));
          }

          chargesSnapshot.docs.forEach(docSnap => {
            const charge = docSnap.data();

            // Only charges — exclude payments/credits/allocation markers.
            const amountPaid = parseFloat(charge.amountPaid) || 0;
            if (amountPaid <= 0) return;
            if (charge.type === "payment" ||
                charge.type === "credit" ||
                charge.type === "payment_allocation") return;

            // Remaining = amount - allocated, with all fallbacks.
            const allocated =
              parseFloat(charge.allocatedAmount) ||
              parseFloat(charge.paidAmount) ||
              (Array.isArray(charge.allocations)
                ? charge.allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
                : 0);
            const remaining = amountPaid - allocated;
            if (remaining <= 0.01) return;

            const cd = new Date(charge.datePaid + "T00:00:00");
            if (isNaN(cd.getTime())) return;

            if (cd >= boundaries[0]) {
              buckets.current += remaining;
            } else if (cd >= boundaries[1]) {
              buckets.days30 += remaining;
            } else if (cd >= boundaries[2]) {
              buckets.days60 += remaining;
            } else if (cd >= boundaries[3]) {
              buckets.days90 += remaining;
            } else {
              buckets.days90plus += remaining;
            }
          });

          Object.keys(buckets).forEach(k => buckets[k] = Number(buckets[k].toFixed(2)));

          const total = this.showCurrent
            ? buckets.current + buckets.days30 + buckets.days60 + buckets.days90 + buckets.days90plus
            : buckets.days30 + buckets.days60 + buckets.days90 + buckets.days90plus;

          reportData.push({
            reference: leaseRef,
            property: propertyName,
            buckets,
            total
          });
        }

        this.reportData = reportData;

        this.totals = {
          current: 0,
          days30: 0,
          days60: 0,
          days90: 0,
          days90plus: 0,
          total: 0
        };
        reportData.forEach(item => {
          this.totals.current += item.buckets.current;
          this.totals.days30 += item.buckets.days30;
          this.totals.days60 += item.buckets.days60;
          this.totals.days90 += item.buckets.days90;
          this.totals.days90plus += item.buckets.days90plus;
          this.totals.total += item.total;
        });
        Object.keys(this.totals).forEach(k => this.totals[k] = Number(this.totals[k].toFixed(2)));

      } catch (error) {
        console.error("Error generating age analysis:", error);
      } finally {
        this.isGenerating = false;
      }
    },

    async fetchLogoBase64() {
      try {
        const logosRef = collection(db, 'Logo');
        const q = query(logosRef, where('id', '==', 2));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const logoDoc = querySnapshot.docs[0];
          const logoData = logoDoc.data();
          this.base64log = logoData.logo2;
        } else {
          console.error('Logo not found');
        }
      } catch (error) {
        console.error("Error fetching logo:", error);
      }
    },

    handleExportSuccess(message) {
      console.log(message);
    },

    handleExportError(error) {
      console.error("Export error:", error);
    },

    formatCurrency(value) {
      if (value === null || value === undefined || value === '' || isNaN(value)) {
        return '';
      }
      return 'R\u00A0' + new Intl.NumberFormat('en-ZA', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    formatDate(date) {
      if (!date || isNaN(new Date(date).getTime())) {
        return '';
      }
      return new Date(date).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },

  watch: {
    landlord: {
      handler() {
        if (this.landlord) {
          this.generateReport();
        }
      },
      immediate: true
    },
    property: {
      handler() {
        if (this.property) {
          this.generateReport();
        }
      },
      immediate: true
    },
    showCurrent() {
      if (this.reportData) {
        this.generateReport();
      }
    }
  },

  created() {
    this.fetchLogoBase64();
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

.compact-table-card {
  border-radius: 8px !important;
  border: 1px solid #e0e0e0 !important;
}

/* Age Analysis Table */
.age-analysis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem !important;
}

.age-analysis-table th,
.age-analysis-table td {
  border: 1px solid #ddd;
  padding: 4px 8px !important;
  font-size: 0.7rem !important;
}

.age-analysis-table th {
  background-color: #f0f0f0 !important;
  font-weight: 700 !important;
  color: #000000 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 0.6rem !important;
  border-bottom: 2px solid #ccc !important;
}

.age-analysis-table td {
  color: #000000 !important;
}

.age-analysis-table .property-column {
  text-align: left;
}

.total-row {
  background-color: #f0f0f0 !important;
  font-weight: bold !important;
}

.total-row td {
  border-top: 2px solid #999 !important;
  font-weight: 700 !important;
}

.negative-balance {
  color: #d32f2f !important;
}

/* Small text field */
.small-text-field :deep(.v-input__slot) {
  min-height: 36px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

/* Small switch */
.small-switch :deep(.v-label) {
  font-size: 0.75rem !important;
  color: #000000 !important;
}

/* Caption class */
.caption {
  font-size: 0.7rem !important;
}

.text-subtitle-2 {
  font-size: 0.85rem !important;
}

/* Buttons */
.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-height: 30px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 16px !important;
}

/* Dialog text */
.v-dialog .v-card-title {
  font-size: 0.9rem !important;
  color: #000000 !important;
}

.v-dialog .v-card-text {
  font-size: 0.75rem !important;
  color: #000000 !important;
}

.v-dialog .v-btn {
  font-size: 0.7rem !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .age-analysis-table th,
  .age-analysis-table td {
    font-size: 0.55rem !important;
    padding: 3px 4px !important;
  }
  
  .age-analysis-table th {
    font-size: 0.5rem !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>